// src/services/companyService.js
//
// UPDATED (invoice printout):
//
//   - Uses the shared `api` instance (services/api.js) instead of its own
//     axios calls. Before, every company request went out with only the
//     cookie: no Bearer token from localStorage, and none of the shared
//     interceptor's handling — no token refresh on a 401, no subscription-
//     locked message on a 402, no "Network error" / "Server error" wording.
//     The base URL logic this file duplicated is the same as api.js's
//     getBaseURL, so requests go to the same place; api also sends
//     withCredentials, so the cookie still travels.
//
//   - uploadLogo / removeLogo for the invoice logo
//     (POST/DELETE /api/companies/:companyId/logo). Both resolve to the full,
//     updated company details, the same shape getCompanyById returns.
//
// Every existing function keeps its name, arguments, return value and
// fallback error message, so no caller changes.
//
// ERRORS. The server's message when there is one (as before). When there is
// none, a network failure or a 5xx keeps the wording api.js already put on the
// error; anything else gets this file's fallback, as before.

import api from './api.js';

/** Rethrow an axios error as an Error with the most useful message. */
function toError(error, fallback) {
  const status = error?.response?.status;
  const serverMessage = error?.response?.data?.message;
  const interceptorMessage = !error?.response || status >= 500 ? error?.message : null;
  const err = new Error(serverMessage || interceptorMessage || fallback);
  err.status = status;
  if (error?.isSubscriptionLocked) {
    err.isSubscriptionLocked = true;
    err.entitlement = error.entitlement;
    err.resubscribeUrl = error.resubscribeUrl;
  }
  return err;
}

const companyService = {
  // Get all companies for a member
  getMemberCompanies: async (memberId) => {
    try {
      const response = await api.get(`/companies/member/${memberId}`);
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to fetch companies');
    }
  },

  // Get company by ID
  getCompanyById: async (companyId) => {
    try {
      const response = await api.get(`/companies/${companyId}`);
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to fetch company');
    }
  },

  // Create a new company
  createCompany: async (companyData) => {
    try {
      const response = await api.post('/companies', companyData);
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to create company');
    }
  },

  // Update company
  updateCompany: async (companyId, companyData) => {
    try {
      const response = await api.put(`/companies/${companyId}`, companyData);
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to update company');
    }
  },

  // Delete company
  deleteCompany: async (companyId) => {
    try {
      const response = await api.delete(`/companies/${companyId}`);
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to delete company');
    }
  },

  // Upload (or replace) the company logo shown on invoices.
  // `file` is a browser File from an <input type="file">; PNG or JPG, 5 MB max
  // (the server refuses anything else). Resolves to the updated company.
  uploadLogo: async (companyId, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await api.post(`/companies/${companyId}/logo`, fd, {
        // Override the instance's JSON default so the BROWSER sets
        // multipart/form-data with its boundary (see imageLibraryService).
        headers: { 'Content-Type': undefined },
      });
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to upload logo');
    }
  },

  // Remove the company logo. Resolves to the updated company.
  removeLogo: async (companyId) => {
    try {
      const response = await api.delete(`/companies/${companyId}/logo`);
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to remove logo');
    }
  },

  // Get all companies (for dropdown lists)
  getAllCompanies: async () => {
    try {
      const response = await api.get('/companies/all');
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to fetch all companies');
    }
  },

  // Reference data endpoints

  // Get company types
  getCompanyTypes: async () => {
    try {
      const response = await api.get('/companies/company-types');
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to fetch company types');
    }
  },

  // Get installation types
  getInstallationTypes: async () => {
    try {
      const response = await api.get('/companies/installation-types');
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to fetch installation types');
    }
  },

  // Get order types for a company
  getCompanyOrderTypes: async (companyId) => {
    try {
      const response = await api.get(`/companies/${companyId}/order-types`);
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to fetch order types');
    }
  },

  // Get order price choices
  getOrderPriceChoices: async () => {
    try {
      const response = await api.get('/companies/order-price-choices');
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to fetch order price choices');
    }
  },

  // Get freight cost bases
  getFreightCostBases: async () => {
    try {
      const response = await api.get('/companies/freight-cost-bases');
      return response.data;
    } catch (error) {
      throw toError(error, 'Failed to fetch freight cost bases');
    }
  }
};

export default companyService;